import { v4 as uuidv4 } from 'uuid';
import type { GetTemplate_template_blueprint } from '@frontend/app/queries/types/GetTemplate';
import { AutomationNodeType } from '@frontend/app/types/globalTypes';
import { GetOffers_offers } from '@frontend/applications/AffiliatesApp/queries/types/GetOffers';
import { OFFER_PRICE_RULE_TYPE, OFFER_TYPE } from '@frontend/applications/AffiliatesApp/types/globalTypes';
import { GetofferByIdWithStats } from '@frontend/applications/AffiliatesApp/queries/types/GetofferByIdWithStats';
import { AutomationNodeV2, AutomationBlueprintV2, AutomationConditionNodeSubType, AutomationVariableV2, AutomationTriggerV2 } from '../types';

/**
 * Utility functions for working with automation tree structures
 */

/**
 * Find a node by ID in the automation tree
 */
export function findNodeById(root: AutomationNodeV2, nodeId: string): AutomationNodeV2 | null {
  if (root.id === nodeId) {
    return root;
  }

  for (const child of root.children) {
    const found = findNodeById(child, nodeId);
    if (found) {
      return found;
    }
  }

  // Also search in elseChildren for condition nodes
  if (root.elseChildren) {
    for (const child of root.elseChildren) {
      const found = findNodeById(child, nodeId);
      if (found) {
        return found;
      }
    }
  }

  return null;
}

/**
 * Find the parent node of a given node ID
 */
export function findParentNode(root: AutomationNodeV2, nodeId: string): AutomationNodeV2 | null {
  for (const child of root.children) {
    if (child.id === nodeId) {
      return root;
    }
    const found = findParentNode(child, nodeId);
    if (found) {
      return found;
    }
  }

  // Also check elseChildren
  if (root.elseChildren) {
    for (const child of root.elseChildren) {
      if (child.id === nodeId) {
        return root;
      }
      const found = findParentNode(child, nodeId);
      if (found) {
        return found;
      }
    }
  }
  return null;
}

/**
 * Get all nodes in the tree as a flat array
 */
export function getAllNodes(root: AutomationNodeV2): AutomationNodeV2[] {
  const nodes: AutomationNodeV2[] = [root];

  for (const child of root.children) {
    nodes.push(...getAllNodes(child));
  }

  // Also include elseChildren
  if (root.elseChildren) {
    for (const child of root.elseChildren) {
      nodes.push(...getAllNodes(child));
    }
  }

  return nodes;
}

/**
 * Get all visible nodes for UI rendering (excludes the hidden root node)
 * This returns only the children of the root and their descendants
 */
export function getVisibleNodes(root: AutomationNodeV2): AutomationNodeV2[] {
  const visibleNodes: AutomationNodeV2[] = [];

  // Get all children of the root (these are visible)
  for (const child of root.children) {
    visibleNodes.push(...getAllNodes(child));
  }

  // Also include elseChildren of root (though root shouldn't have elseChildren typically)
  if (root.elseChildren) {
    for (const child of root.elseChildren) {
      visibleNodes.push(...getAllNodes(child));
    }
  }

  return visibleNodes;
}

/**
 * Get the depth of a node in the tree
 */
export function getNodeDepth(root: AutomationNodeV2, targetNodeId: string): number {
  if (root.id === targetNodeId) {
    return 0;
  }

  for (const child of root.children) {
    const depth = getNodeDepth(child, targetNodeId);
    if (depth !== -1) {
      return depth + 1;
    }
  }

  return -1;
}

/**
 * Add a node as a child to a parent node
 */
export function addChildNode(
  root: AutomationNodeV2,
  parentId: string,
  newNode: AutomationNodeV2,
  index?: number,
  isElseBranch?: boolean
): AutomationNodeV2 {
  if (root.id === parentId) {
    if (isElseBranch) {
      // Add to elseChildren
      const newElseChildren = [...(root.elseChildren || [])];
      if (index !== undefined) {
        newElseChildren.splice(index, 0, newNode);
      } else {
        newElseChildren.push(newNode);
      }

      // If this is the first time adding to elseChildren and it's a CONDITION,
      // convert it to IF_ELSE_CONDITION
      let updatedSubType = root.subType;
      if (root.type === AutomationNodeType.CONDITION && root.subType !== AutomationConditionNodeSubType.IF_ELSE_CONDITION) {
        updatedSubType = AutomationConditionNodeSubType.IF_ELSE_CONDITION;
      }

      return {
        ...root,
        subType: updatedSubType,
        elseChildren: newElseChildren,
      };
    } else {
      // Add to children (default behavior)
      const newChildren = [...root.children];
      if (index !== undefined) {
        newChildren.splice(index, 0, newNode);
      } else {
        newChildren.push(newNode);
      }
      return {
        ...root,
        children: newChildren,
      };
    }
  }

  return {
    ...root,
    children: root.children.map((child) => addChildNode(child, parentId, newNode, index, isElseBranch)),
    elseChildren: root.elseChildren?.map((child) => addChildNode(child, parentId, newNode, index, isElseBranch)),
  };
}

/**
 * Remove a node from the tree
 */
export function removeNode(root: AutomationNodeV2, nodeId: string): AutomationNodeV2 | null {
  if (root.id === nodeId) {
    return null; // Can't remove root
  }

  return {
    ...root,
    children: root.children
      .map((child) => removeNode(child, nodeId))
      .filter((child) => child !== null) as AutomationNodeV2[],
    elseChildren: root.elseChildren
      ?.map((child) => removeNode(child, nodeId))
      .filter((child) => child !== null) as AutomationNodeV2[],
  };
}

/**
 * Helper function to check if a node type is linear (non-branching)
 */
function isLinearNode(nodeType: AutomationNodeType): boolean {
  return nodeType === AutomationNodeType.ACTION || nodeType === AutomationNodeType.DELAY;
}

/**
 * Helper function to determine condition node branch types
 */
function getConditionBranchType(node: AutomationNodeV2): 'both' | 'then-only' | 'else-only' | 'none' {
  if (node.type !== AutomationNodeType.CONDITION) {
    return 'none';
  }

  const hasThen = node.children && node.children.length > 0;
  const hasElse = node.elseChildren && node.elseChildren.length > 0;

  if (hasThen && hasElse) {
    return 'both';
  } else if (hasThen) {
    return 'then-only';
  } else if (hasElse) {
    return 'else-only';
  } else {
    return 'none';
  }
}

/**
 * Delete entire path starting from the specified node (removes node and all its descendants)
 * This is the simpler deletion method that doesn't preserve children
 */
export function deletePath(root: AutomationNodeV2, nodeId: string): AutomationNodeV2 | null {
  if (root.id === nodeId) {
    return null; // Can't delete root
  }

  return {
    ...root,
    children: root.children
      .map((child) => deletePath(child, nodeId))
      .filter((child) => child !== null) as AutomationNodeV2[],
    elseChildren: root.elseChildren
      ?.map((child) => deletePath(child, nodeId))
      .filter((child) => child !== null) as AutomationNodeV2[],
  };
}

/**
 * Enhanced delete node that preserves children based on node type and branching logic
 */
export function deleteNodeWithChildPreservation(root: AutomationNodeV2, nodeId: string): AutomationNodeV2 | null {
  // Can't delete root node
  if (root.id === nodeId) {
    return root;
  }

  // Find the node to delete and its parent
  const nodeToDelete = findNodeById(root, nodeId);
  if (!nodeToDelete) {
    return root; // Node not found, return unchanged
  }

  const parent = findParentNode(root, nodeId);
  if (!parent) {
    return root; // Parent not found, return unchanged
  }

  // Determine which branch the node is in
  const isInElseBranch = parent.elseChildren?.some((child) => child.id === nodeId) || false;
  const targetChildren = isInElseBranch ? parent.elseChildren : parent.children;
  const nodeIndex = targetChildren.findIndex((child) => child.id === nodeId);

  if (nodeIndex === -1) {
    return root; // Node not found in expected branch
  }

  let newChildren: AutomationNodeV2[];

  // Apply deletion logic based on node type
  if (isLinearNode(nodeToDelete.type)) {
    // Linear node: preserve children by moving them up to parent
    const preservedChildren = nodeToDelete.children || [];
    newChildren = [
      ...targetChildren.slice(0, nodeIndex),
      ...preservedChildren,
      ...targetChildren.slice(nodeIndex + 1)
    ];
  } else if (nodeToDelete.type === AutomationNodeType.CONDITION) {
    const branchType = getConditionBranchType(nodeToDelete);

    switch (branchType) {
      case 'both':
        // Both branches present: delete entire subtree (current behavior)
        newChildren = [
          ...targetChildren.slice(0, nodeIndex),
          ...targetChildren.slice(nodeIndex + 1)
        ];
        break;

      case 'then-only':
        // Only Then branch: replace condition with its Then subtree
        newChildren = [
          ...targetChildren.slice(0, nodeIndex),
          ...nodeToDelete.children,
          ...targetChildren.slice(nodeIndex + 1)
        ];
        break;

      case 'else-only':
        // Only Else branch: replace condition with its Else subtree
        newChildren = [
          ...targetChildren.slice(0, nodeIndex),
          ...nodeToDelete.elseChildren || [],
          ...targetChildren.slice(nodeIndex + 1)
        ];
        break;

      case 'none':
      default:
        // No children: delete normally
        newChildren = [
          ...targetChildren.slice(0, nodeIndex),
          ...targetChildren.slice(nodeIndex + 1)
        ];
        break;
    }
  } else {
    // Unknown node type: delete normally without preserving children
    newChildren = [
      ...targetChildren.slice(0, nodeIndex),
      ...targetChildren.slice(nodeIndex + 1)
    ];
  }

  // Update the parent node with new children
  const updatedParent = isInElseBranch
    ? { ...parent, elseChildren: newChildren }
    : { ...parent, children: newChildren };

  // Return the tree with the updated parent
  return updateNode(root, parent.id, updatedParent);
}

/**
 * Update a specific node in the tree
 */
export function updateNode(
  root: AutomationNodeV2,
  nodeId: string,
  updates: Partial<AutomationNodeV2>
): AutomationNodeV2 {
  if (root.id === nodeId) {
    return {
      ...root,
      ...updates,
      // Preserve children and elseChildren if not explicitly updated
      children: updates.children || root.children,
      elseChildren: updates.elseChildren !== undefined ? updates.elseChildren : root.elseChildren,
    };
  }

  return {
    ...root,
    children: root.children.map((child) => updateNode(child, nodeId, updates)),
    elseChildren: root.elseChildren?.map((child) => updateNode(child, nodeId, updates)),
  };
}

/**
 * Move a node from one parent to another
 */
export function moveNode(
  root: AutomationNodeV2,
  nodeId: string,
  newParentId: string,
  index?: number
): AutomationNodeV2 {
  const nodeToMove = findNodeById(root, nodeId);
  if (!nodeToMove || nodeId === newParentId) {
    return root;
  }

  // Remove the node from its current location
  const withoutNode = removeNode(root, nodeId);
  if (!withoutNode) {
    return root;
  }

  // Add it to the new parent
  return addChildNode(withoutNode, newParentId, nodeToMove, index);
}

/**
 * Clone a node (deep copy)
 */
export function cloneNode(node: AutomationNodeV2, generateNewIds = true): AutomationNodeV2 {
  const cloned: AutomationNodeV2 = {
    ...node,
    id: generateNewIds ? generateNodeId() : node.id,
    edgeLabel: node.edgeLabel, // Preserve edge label when cloning
    metadata: { ...node.metadata },
    variables: node.variables.map((v) => ({ ...v })),
    children: node.children.map((child) => cloneNode(child, generateNewIds)),
    elseChildren: node.elseChildren?.map((child) => cloneNode(child, generateNewIds)),
  };

  return cloned;
}

/**
 * Add a node to the else branch of a condition node
 */
export function addElseChildNode(
  root: AutomationNodeV2,
  parentId: string,
  newNode: AutomationNodeV2,
  index?: number
): AutomationNodeV2 {
  if (root.id === parentId && root.type === AutomationNodeType.CONDITION) {
    const newElseChildren = [...(root.elseChildren || [])];
    if (index !== undefined) {
      newElseChildren.splice(index, 0, newNode);
    } else {
      newElseChildren.push(newNode);
    }
    return {
      ...root,
      elseChildren: newElseChildren,
    };
  }

  return {
    ...root,
    children: root.children.map((child) => addElseChildNode(child, parentId, newNode, index)),
    elseChildren: root.elseChildren?.map((child) => addElseChildNode(child, parentId, newNode, index)),
  };
}

/**
 * Determine if a node is in the else branch of its parent
 */
export function isInElseBranch(root: AutomationNodeV2, nodeId: string): boolean {
  const parent = findParentNode(root, nodeId);
  if (!parent || !parent.elseChildren) {
    return false;
  }
  return parent.elseChildren.some((child) => child.id === nodeId);
}

/**
 * Generate a unique node ID
 */
export function generateNodeId(): string {
  return uuidv4();
}

/**
 * Ensure all nodes in the tree have an id field (required by API)
 * Generates UUID for nodes that don't have one
 */
export function ensureNodeIds(node: AutomationNodeV2): AutomationNodeV2 {
  return {
    ...node,
    id: node.id || generateNodeId(),
    children: node.children.map(ensureNodeIds),
    elseChildren: node.elseChildren?.map(ensureNodeIds),
  };
}

/**
 * Ensure all condition nodes have the correct subType based on their structure
 * Sets subType to IF_ELSE_CONDITION if the condition node has elseChildren
 */
export function ensureConditionSubTypes(node: AutomationNodeV2): AutomationNodeV2 {
  const updatedNode = {
    ...node,
    children: node.children.map(ensureConditionSubTypes),
    elseChildren: node.elseChildren?.map(ensureConditionSubTypes),
  };

  // If this is a CONDITION node with elseChildren, ensure it has the correct subType
  if (updatedNode.type === AutomationNodeType.CONDITION && updatedNode.elseChildren && updatedNode.elseChildren.length > 0) {
    return {
      ...updatedNode,
      subType: AutomationConditionNodeSubType.IF_ELSE_CONDITION,
    };
  }

  return updatedNode;
}

/**
 * Ensure the root node is properly configured with at least 1ms delay to pass validation
 * If root has no delay configured, set it to 1ms (hidden root)
 * If root has delay > 1ms, preserve it (visible delay node)
 */
export function ensureHiddenRootConfiguration(node: AutomationNodeV2): AutomationNodeV2 {
  const updatedNode = {
    ...node,
    children: node.children.map(ensureConditionSubTypes),
    elseChildren: node.elseChildren?.map(ensureConditionSubTypes),
  };

  // If this is a DELAY node (which the root should be)
  if (updatedNode.type === AutomationNodeType.DELAY) {
    // Only set to 1ms if not configured or configured as 0
    // Preserve any value >= 100ms
    const currentDuration = updatedNode.metadata?.durationMs;
    if (!currentDuration || currentDuration === 1) {
      return {
        ...updatedNode,
        metadata: {
          ...updatedNode.metadata,
          durationMs: 1, // Auto-configure with 1ms delay for hidden root
        },
      };
    }
    // Keep existing duration if >= 100ms (visible delay node)
  }

  return updatedNode;
}

/**
 * Insert a node inline between parent and its existing children
 * This creates a linear chain: Parent → NewNode → ExistingChildren
 */
export function insertNodeInline(
  root: AutomationNodeV2,
  parentId: string,
  newNode: AutomationNodeV2,
  isElseBranch?: boolean
): AutomationNodeV2 {
  if (root.id === parentId) {
    if (isElseBranch) {
      // Move existing elseChildren to become children of the new node
      const newNodeWithChildren = {
        ...newNode,
        children: root.elseChildren || [],
        elseChildren: newNode.elseChildren,
      };

      const updatedRoot = {
        ...root,
        elseChildren: [newNodeWithChildren],
      };

      // Don't set subType here - let ensureConditionSubTypes handle it during save
      return updatedRoot;
    } else {
      // Move existing children to become children of the new node
      const newNodeWithChildren = {
        ...newNode,
        children: root.children,
        elseChildren: newNode.elseChildren,
      };

      return {
        ...root,
        children: [newNodeWithChildren],
      };
    }
  }

  return {
    ...root,
    children: root.children.map((child) => insertNodeInline(child, parentId, newNode, isElseBranch)),
    elseChildren: root.elseChildren?.map((child) => insertNodeInline(child, parentId, newNode, isElseBranch)),
  };
}

/**
 * Add a node as a new branch to a parent node without affecting existing children
 * This creates multiple outgoing paths: Parent → [ExistingChildren, NewNode]
 * The existing edge A → B remains intact, and a new branch A → NewNode is created
 */
export function addBranchNode(
  root: AutomationNodeV2,
  parentId: string,
  newNode: AutomationNodeV2,
  isElseBranch?: boolean
): AutomationNodeV2 {
  if (root.id === parentId) {
    if (isElseBranch) {
      // Add to elseChildren without affecting existing elseChildren
      const newElseChildren = [...(root.elseChildren || []), newNode];

      // If this is the first time adding to elseChildren and it's a CONDITION,
      // convert it to IF_ELSE_CONDITION
      let updatedSubType = root.subType;
      if (root.type === AutomationNodeType.CONDITION && root.subType !== AutomationConditionNodeSubType.IF_ELSE_CONDITION) {
        updatedSubType = AutomationConditionNodeSubType.IF_ELSE_CONDITION;
      }

      return {
        ...root,
        subType: updatedSubType,
        elseChildren: newElseChildren,
      };
    } else {
      // Add to children without affecting existing children
      const newChildren = [...root.children, newNode];
      return {
        ...root,
        children: newChildren,
      };
    }
  }

  return {
    ...root,
    children: root.children.map((child) => addBranchNode(child, parentId, newNode, isElseBranch)),
    elseChildren: root.elseChildren?.map((child) => addBranchNode(child, parentId, newNode, isElseBranch)),
  };
}

/**
 * Replace a placeholder node with a real node while preserving its position in the tree
 * This is used when a user selects a node type from a placeholder
 */
export function replacePlaceholderNode(
  root: AutomationNodeV2,
  placeholderId: string,
  newNode: AutomationNodeV2
): AutomationNodeV2 {
  if (root.id === placeholderId) {
    // Replace the root node (though this shouldn't happen in practice)
    return {
      ...newNode,
      children: root.children, // Preserve any existing children
      elseChildren: root.elseChildren,
    };
  }

  return {
    ...root,
    children: root.children.map((child) => {
      if (child.id === placeholderId) {
        // Replace this child with the new node, preserving children
        return {
          ...newNode,
          children: child.children,
          elseChildren: child.elseChildren,
        };
      }
      return replacePlaceholderNode(child, placeholderId, newNode);
    }),
    elseChildren: root.elseChildren?.map((child) => {
      if (child.id === placeholderId) {
        // Replace this elseChild with the new node, preserving children
        return {
          ...newNode,
          children: child.children,
          elseChildren: child.elseChildren,
        };
      }
      return replacePlaceholderNode(child, placeholderId, newNode);
    }),
  };
}

/**
 * Validate the automation tree structure
 */
export function validateAutomationTree(root: AutomationNodeV2): string[] {
  const errors: string[] = [];
  const visitedIds = new Set<string>();

  function validateNode(node: AutomationNodeV2, path: string): void {
    // Check for duplicate IDs
    if (visitedIds.has(node.id)) {
      errors.push(`Duplicate node ID found: ${node.id} at ${path}`);
    }
    visitedIds.add(node.id);

    // Check for circular references (basic check)
    if (path.split('/').filter((id) => id === node.id).length > 1) {
      errors.push(`Circular reference detected for node: ${node.id}`);
    }

    // Validate children
    node.children.forEach((child) => {
      validateNode(child, `${path}/${child.id}`);
    });

    // Type-specific validations (relaxed for development)
    switch (node.type) {
      case AutomationNodeType.CONDITION:
        // Allow condition nodes without configuration for now
        // if (!node.metadata.condition) {
        //   errors.push(`Condition node ${node.id} missing condition metadata`);
        // }
        break;
      case AutomationNodeType.ACTION:
        if (!node.subType) {
          errors.push(`Action node ${node.id} missing subType`);
        }
        break;
      case AutomationNodeType.DELAY: {
        // For hidden root nodes (depth 0), allow >= 1ms delay
        // For other delay nodes, require configuration > 0
        const isHiddenRoot = path === node.id; // Root node path is just its own ID
        if (!isHiddenRoot && (!node.metadata.durationMs || node.metadata.durationMs <= 0)) {
          errors.push('Delay node: duration/time must be configured and greater than 0');
        }
        //  else if (isHiddenRoot && node.metadata.durationMs !== 1) {
        //   // For hidden root, ensure it's exactly 1ms
        //   errors.push('Hidden root delay node should have exactly 1ms duration');
        // }
        break;
      }
    }
  }

  validateNode(root, root.id);
  return errors;
}

/**
 * Add a node as a child to the hidden root node
 * This is used when adding nodes directly to the trigger (which targets the hidden root)
 * @param isInlineInsertion - If true, inserts node inline (pushes existing children down one level)
 *                           If false, adds as parallel branch (appends to children array)
 */
export function addNodeToHiddenRoot(
  trigger: AutomationTriggerV2,
  newNode: AutomationNodeV2,
  isInlineInsertion: boolean = false
): AutomationTriggerV2 {
  if (!trigger.root) {
    // Create a default root node if it doesn't exist
    const rootNode: AutomationNodeV2 = {
      id: generateNodeId(),
      type: AutomationNodeType.DELAY,
      children: [newNode],
      metadata: { durationMs: 1 }, // Auto-configure with 1ms delay for hidden root
      variables: [],
    };

    return {
      ...trigger,
      root: rootNode,
    };
  }

  let updatedRoot: AutomationNodeV2;

  if (isInlineInsertion) {
    // Inline insertion: new node becomes root's direct child, existing children become new node's children
    const newNodeWithExistingChildren = {
      ...newNode,
      children: [...trigger.root.children], // Move existing children to new node
    };

    updatedRoot = {
      ...trigger.root,
      children: [newNodeWithExistingChildren], // New node is now the only direct child
    };
  } else {
    // Branch addition: add new node as parallel child (existing behavior)
    updatedRoot = {
      ...trigger.root,
      children: [...trigger.root.children, newNode],
    };
  }

  return {
    ...trigger,
    root: updatedRoot,
  };
}

// Use existing GraphQL types for templates
type TemplateBlueprint = GetTemplate_template_blueprint;
type TemplateNode = GetTemplate_template_blueprint['triggers'][0]['root'];

/**
 * Convert template blueprint to V2 structure with proper node rendering
 */
export function convertTemplateToV2Blueprint(templateBlueprint: TemplateBlueprint, projectId: number): AutomationBlueprintV2 {
  return {
    id: templateBlueprint.id,
    name: templateBlueprint.name || 'Untitled Automation',
    description: templateBlueprint.description || '',
    triggers: templateBlueprint.triggers?.map((trigger) => ({
      id: trigger.id || generateNodeId(),
      type: trigger.type,
      root: convertTemplateNodeToV2(trigger.root),
      variables: trigger.variables?.map((variable): AutomationVariableV2 => ({
        id: variable.id,
        name: variable.name,
        type: variable.type,
        value: variable.value || null,
      })) || [],
      metadata: trigger.metadata || {},
    })) || [],
    programId: projectId,
    templateId: templateBlueprint.templateId,
    metadata: templateBlueprint.metadata || { icon: 'workflow' },
  };
}

/**
 * Convert template node to V2 structure with proper nesting and IDs
 */
function convertTemplateNodeToV2(templateNode: TemplateNode): AutomationNodeV2 {
  if (!templateNode) {
    throw new Error('Template node is required');
  }

  return {
    id: templateNode.id || generateNodeId(),
    type: templateNode.type,
    subType: templateNode.subType || null,
    edgeLabel: (templateNode as any).edgeLabel, // Preserve edge label from template (type will be updated after codegen)
    children: Array.isArray(templateNode.children)
      ? templateNode.children.map(convertTemplateNodeToV2)
      : [],
    // Handle else children for condition nodes (not present in GraphQL schema)
    elseChildren: undefined,
    metadata: {
      ...templateNode.metadata,
      // Ensure proper metadata structure for different node types
      ...(templateNode.type === AutomationNodeType.DELAY && templateNode.metadata?.durationMs !== undefined
        ? { durationMs: templateNode.metadata.durationMs }
        : {}),
      ...(templateNode.type === AutomationNodeType.CONDITION && templateNode.metadata?.condition
        ? { condition: templateNode.metadata.condition }
        : {}),
      ...(templateNode.type === AutomationNodeType.ACTION
        ? {
            resourceId: templateNode.metadata?.resourceId || null,
            templateId: templateNode.metadata?.templateId || null,
            fromPreviousSender: templateNode.metadata?.fromPreviousSender || false,
            boostId: templateNode.metadata?.boostId || null,
          }
        : {}),
    },
    variables: templateNode.variables?.map?.((variable): AutomationVariableV2 => ({
      id: variable.id,
      name: variable.name,
      type: variable.type,
      value: variable.value || null,
    })) || [],
  };
}

// Helper functions for offer details
export interface OfferIconData {
  iconType: 'tags' | 'link' | 'shopping-cart';
  bgColor: string;
}

export const getOfferIconData = (offer: GetOffers_offers): OfferIconData => {
  if (offer.type === 'PROMO_CODE') {
    return {
      iconType: 'tags',
      bgColor: 'bg-blue-4'
    };
  }
  if (offer.type === 'LINK') {
    return {
      iconType: 'link',
      bgColor: 'bg-magenta-4'
    };
  }
  return {
    iconType: 'shopping-cart',
    bgColor: 'bg-blue-4'
  };
};

  // Helper functions to extract detailed offer information
  export const getOfferDiscountAmount = (offerDetails: GetofferByIdWithStats['offer']) => {
    if (!offerDetails) return null;

    // For link offers, show the URL
    if (offerDetails.offer.type === OFFER_TYPE.LINK && offerDetails.offer.links && offerDetails.offer.links.length > 0) {
      const link = offerDetails.offer.links[0];
      if (link.url) {
        // Extract domain from URL for display
        try {
          const url = new URL(link.url);
          return url.hostname;
        } catch {
          return link.url;
        }
      }
    }

    // Get discount from promo codes for promo code offers
    if (offerDetails.offer.promos && offerDetails.offer.promos.length > 0) {
      const promo = offerDetails.offer.promos[0];
      if (promo.priceRuleType === OFFER_PRICE_RULE_TYPE.PERCENTAGE) {
        return `${promo.priceRuleAmount}% Discount`;
      } else if (promo.priceRuleType === OFFER_PRICE_RULE_TYPE.AMOUNT) {
        return `$${promo.priceRuleAmount} Off`;
      }
    }

    // Get payout information as fallback
    if (offerDetails.offer.payouts && offerDetails.offer.payouts.length > 0) {
      const payout = offerDetails.offer.payouts[0];
      if (payout.percentPayout) {
        return `${payout.percentPayout}% Commission`;
      } else if (payout.flatPayout) {
        return `$${payout.flatPayout} Commission`;
      }
    }

    return 'Offer Details';
  };

/**
 * Update nodes with null templateId to use the newly created template data
 */
export function updateNodesWithNewTemplateId(
  root: AutomationNodeV2,
  newTemplateId: number,
  templateData?: { title?: string; subject?: string }
): AutomationNodeV2 {
  const updatedNode = { ...root };

  // Update current node if it's an ACTION node with null templateId
  // This includes both SEND_EMAIL and ADD_TO_OFFER actions that have email enabled
  if (updatedNode.type === AutomationNodeType.ACTION && updatedNode.metadata?.templateId === null) {
    updatedNode.metadata = {
      ...updatedNode.metadata,
      templateId: newTemplateId,
      // Update template name and subject if provided
      ...(templateData?.title && { templateName: templateData.title }),
      ...(templateData?.subject && { templateSubject: templateData.subject }),
    };
  }

  // Recursively update children
  if (updatedNode.children) {
    updatedNode.children = updatedNode.children.map((child) =>
      updateNodesWithNewTemplateId(child, newTemplateId, templateData)
    );
  }

  // Recursively update elseChildren for condition nodes
  if (updatedNode.elseChildren) {
    updatedNode.elseChildren = updatedNode.elseChildren.map((child) =>
      updateNodesWithNewTemplateId(child, newTemplateId, templateData)
    );
  }

  return updatedNode;
}
